"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClerkProvider = void 0;
const tslib_1 = require("tslib");
const clerk_react_1 = require("@clerk/clerk-react");
const react_1 = tslib_1.__importDefault(require("react"));
const utils_1 = require("../utils");
const useAwaitableNavigate_1 = require("./useAwaitableNavigate");
tslib_1.__exportStar(require("@clerk/clerk-react"), exports);
/**
 * Remix hydration errors should not stop Clerk navigation from working, as the components mount only after
 * hydration is done (in the case of a hydration error, the components will simply mount after client-side hydration)
 * In the case of a hydration error, the first `navigate` function we get from the `useNavigate` hook will not work
 * because the RemixClerkProvider (which is part of the host app) will unmount before the following useEffect within `navigate` fires:
 * https://github.com/remix-run/react-router/blob/main/packages/react-router/lib/hooks.tsx#L175
 * so isomorphicClerk will initialise with a `navigate` function that will never have `activeRef.current` set to true.
 * This variable is just an object ref/cache outside the React rendering cycle that holds a reference to the
 * latest `navigate` function. After a hydration error occurs, RemixClerkProvider will *remount* and this variable
 * will finally get a `navigate` function that has a `activeRef.current` to true so navigation will function as it should.
 */
const awaitableNavigateRef = { current: undefined };
function ClerkProvider({ children, ...rest }) {
    const awaitableNavigate = (0, useAwaitableNavigate_1.useAwaitableNavigate)();
    react_1.default.useEffect(() => {
        awaitableNavigateRef.current = awaitableNavigate;
    }, [awaitableNavigate]);
    const { clerkState, ...restProps } = rest;
    clerk_react_1.ClerkProvider.displayName = 'ReactClerkProvider';
    (0, utils_1.assertValidClerkState)(clerkState);
    const { __clerk_ssr_state, __frontendApi, __publishableKey, __proxyUrl, __domain, __isSatellite, __clerk_debug, __signInUrl, __clerkJSUrl, __clerkJSVersion, } = (clerkState === null || clerkState === void 0 ? void 0 : clerkState.__internal_clerk_state) || {};
    react_1.default.useEffect(() => {
        (0, utils_1.warnForSsr)(clerkState);
    }, []);
    react_1.default.useEffect(() => {
        window.__clerk_debug = __clerk_debug;
    }, []);
    return (react_1.default.createElement(clerk_react_1.ClerkProvider, { navigate: (to) => { var _a; return (_a = awaitableNavigateRef.current) === null || _a === void 0 ? void 0 : _a.call(awaitableNavigateRef, to); }, initialState: __clerk_ssr_state, frontendApi: __frontendApi, publishableKey: __publishableKey, proxyUrl: __proxyUrl, domain: __domain, isSatellite: __isSatellite, signInUrl: __signInUrl, clerkJSUrl: __clerkJSUrl, clerkJSVersion: __clerkJSVersion, ...restProps }, children));
}
exports.ClerkProvider = ClerkProvider;
